import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import { FiCopy, FiCheck, FiExternalLink, FiCreditCard, FiBriefcase, FiLink, FiArrowRight, FiDollarSign, FiUser } from 'react-icons/fi';
import { toast } from 'react-hot-toast';

export default function MemberCardScreen() {
  const { user, userType } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);

  const isProvider = userType === 'provider';

  useEffect(() => {
    loadProfile();
  }, [user, userType]);

  const loadProfile = async () => {
    if (!user) return;
    try {
      let data = null;
      if (isProvider) {
        data = await supabaseService.getProviderById(user.id);
      } else {
        data = await supabaseService.getClientById(user.id);
      }
      console.log('Loaded Profile Data:', data); // للتحقق من البيانات في الـ Console
      setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('فشل تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  // --- Helper Functions ---

  const displayName = (profile?.fullName || profile?.full_name || user?.user_metadata?.full_name || user?.user_metadata?.name || 'USER NAME').toUpperCase();

  const getCardNumber = () => {
    const raw = (profile?.cardNumber || profile?.card_number || '0000000000000000')
      .toString()
      .trim()
      .replace(/\s/g, '')
      .padEnd(16, '0')
      .substring(0, 16);
    return `${raw.substring(0,4)}  ${raw.substring(4,8)}  ${raw.substring(8,12)}  ${raw.substring(12,16)}`;
  };

  const getValidSince = () => {
    const dateStr = profile?.createdAt || profile?.created_at || user?.created_at;
    const d = dateStr ? new Date(dateStr) : new Date();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear().toString().substring(2);
    return `${month}/${year}`;
  };

  const memberId = isProvider
    ? (profile?.providerId || profile?.provider_id || '---')
    : (profile?.clientId || profile?.client_id || '---');

  // ✅ استخراج الرصيد بأمان (يدعم camelCase و snake_case)
  const walletBalance = profile?.wallet ?? profile?.wallet_balance ?? profile?.walletBalance ?? profile?.availableBalance ?? 0;

  // ✅ استخراج الكود بأمان
  const walletCode = profile?.walletCode || profile?.wallet_code || null;

  const profileLink = isProvider
    ? `https://khidma4u.netlify.app/provider-public/${user.id}`
    : `https://khidma4u.netlify.app/client-public/${user.id}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(profileLink);
      setLinkCopied(true);
      toast.success('تم نسخ الرابط');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      toast.error('فشل النسخ');
    }
  };

  const openPublicProfile = () => {
    if (isProvider) {
      navigate(`/provider-public/${user.id}`);
    } else {
      navigate(`/client-public/${user.id}`);
    }
  };

  const openWallet = () => {
    navigate('/wallet');
  };

  const openBankAccount = () => {
    navigate('/provider/bank-account');
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
    </div>
  );

  return (
    <div className="h-screen w-full bg-gray-50 font-sans flex flex-col overflow-hidden" dir="rtl">

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 lg:px-10 py-3 flex items-center gap-3 shadow-sm shrink-0">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
          <FiArrowRight className="text-gray-700" />
        </button>
        <h1 className="text-lg font-bold text-gray-900">
          {isProvider ? 'بطاقتي المهنية' : 'بطاقتي الشخصية'}
        </h1>
      </div>

      {/* Main content — fills remaining height, no scroll */}
      <div className="flex-1 min-h-0 px-6 lg:px-10 py-5 lg:py-8">
        <div className="h-full max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">

          {/* ── العمود الأيمن: البطاقة + المحفظة ── */}
          <div className="lg:col-span-3 flex flex-col gap-5 min-h-0">

            {/* البطاقة الرئيسية */}
            <div className="w-full flex-1 lg:flex-none lg:aspect-[1.75/1] rounded-[20px] shadow-2xl relative overflow-hidden"
                 style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #141414 50%, #1C1C1C 100%)' }}>

              <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-white opacity-[0.02] rounded-full"></div>
              <div className="absolute -top-10 -left-10 w-32 h-32 bg-white opacity-[0.03] rounded-full"></div>

              <div className="relative z-10 h-full p-6 lg:p-8 flex flex-col justify-between text-white">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-xl lg:text-2xl font-extrabold tracking-[0.15em]">{isProvider ? 'KHIDMA PRO' : 'KHIDMA'}</h2>
                    <p className="text-[10px] text-gray-400 tracking-[0.25em] mt-1 font-light">
                      {isProvider ? 'PRO CARD' : 'MEMBER CARD'}
                    </p>
                  </div>
                  <div className="w-12 h-12 border border-white/20 rounded-xl grid grid-cols-2 gap-1 p-1.5 opacity-50">
                    <div className="bg-white rounded-sm"></div>
                    <div className="bg-white rounded-sm"></div>
                    <div className="bg-white rounded-sm"></div>
                    <div className="bg-white rounded-sm"></div>
                  </div>
                </div>

                <div className="my-4">
                  <p className="text-lg lg:text-2xl tracking-[0.2em] font-mono text-center">{getCardNumber()}</p>
                </div>

                <div className="flex justify-between items-end text-xs">
                  <div>
                    <p className="text-gray-500 text-[10px] tracking-wider mb-1">VALID SINCE</p>
                    <p className="font-bold text-sm">{getValidSince()}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-gray-500 text-[10px] tracking-wider mb-1">
                      {isProvider ? 'PROVIDER ID' : 'MEMBER ID'}
                    </p>
                    <p className="font-bold text-sm">{memberId}</p>
                  </div>
                  <div className="text-left">
                    <p className="text-gray-500 text-[10px] tracking-wider mb-1">CARDHOLDER</p>
                    <p className="font-bold text-sm truncate max-w-[160px]">{displayName}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* قسم الرابط الشخصي + الحساب البنكي جنبًا إلى جنب */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-[18px] border border-gray-200 shadow-sm flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">
                    <FiLink />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">رابطك الشخصي</p>
                    <p className="text-sm text-gray-800 truncate">{profileLink}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={copyLink}
                    className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all duration-200 flex items-center justify-center gap-2
                      ${linkCopied ? 'bg-green-100 text-green-700' : 'bg-gray-900 text-white'}`}
                  >
                    {linkCopied ? <FiCheck size={13} /> : <FiCopy size={13} />}
                    {linkCopied ? 'تم النسخ!' : 'نسخ الرابط'}
                  </button>
                  <button
                    onClick={openPublicProfile}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-gray-700 font-medium text-xs flex items-center justify-center gap-2 hover:bg-gray-50"
                  >
                    <FiExternalLink size={13} />
                    معاينة
                  </button>
                </div>
              </div>

              {isProvider && (
                <button
                  onClick={openBankAccount}
                  className="w-full p-4 rounded-[18px] bg-white border border-gray-200 flex items-center gap-4 shadow-sm active:bg-gray-50 transition-colors"
                >
                  <div className="w-12 h-12 shrink-0 rounded-xl bg-gray-100 text-gray-600 flex items-center justify-center">
                    <FiDollarSign size={22} />
                  </div>
                  <div className="flex-1 text-right min-w-0">
                    <h3 className="font-bold text-gray-800 text-sm">ربط الحساب البنكي / CCP</h3>
                    <p className="text-xs text-gray-500 truncate">أضف رقم RIB وصورة الشيك المشطوب</p>
                  </div>
                  <FiArrowRight className="text-gray-400 shrink-0" />
                </button>
              )}
            </div>
          </div>

          {/* ── العمود الأيسر: المحفظة + التنبيه ── */}
          <div className="lg:col-span-2 flex flex-col gap-5 min-h-0">

            {/* زر المحفظة */}
            <button
              onClick={openWallet}
              className="w-full flex-1 p-6 rounded-xl bg-black text-white flex flex-col justify-center gap-4 shadow-lg active:scale-[0.98] transition-transform"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 shrink-0 rounded-xl bg-white/10 flex items-center justify-center border border-white/10">
                  <FiBriefcase size={22} />
                </div>
                <div className="flex-1 text-right min-w-0">
                  <h3 className="font-bold text-base">محفظتي</h3>
                  <p className="text-sm text-gray-400">
                    {walletBalance.toFixed(2)} دج
                  </p>
                </div>
                {walletCode && (
                  <div className="text-center shrink-0">
                    <p className="text-[10px] text-gray-500">الكود</p>
                    <p className="font-mono font-bold text-lg tracking-wider">{walletCode}</p>
                  </div>
                )}
                <FiArrowRight className="opacity-50 text-sm shrink-0" />
              </div>
            </button>

            {/* تنبيه */}
            <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl flex gap-3 shrink-0">
              <div className="text-blue-400 mt-0.5 shrink-0">
                <FiUser size={18} />
              </div>
              <p className="text-xs text-blue-800 leading-relaxed">
                {isProvider
                  ? 'شارك رابطك مع عملائك ليتمكنوا من التحقق من هويتك.'
                  : 'استخدم رابطك الشخصي لمشاركة ملفك مع الآخرين.'}
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}